import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Copy, Clock, Heart, KeyRound, PauseCircle, PlayCircle, Plus, Sparkles,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyForm = { campus_id: "", note: "", expires_in_days: 30, max_uses: "" };

function daysFromNow(days) {
  if (!days) return null;
  const d = new Date();
  d.setDate(d.getDate() + Number(days));
  return d.toISOString();
}

export default function VolunteerInvitesAdmin() {
  const [invites, setInvites] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [showRevoked, setShowRevoked] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/volunteer-invites");
      setInvites(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campuses");
        setCampuses(data);
      } catch (err) {
        console.error("campus dropdown load failed", err);
        toast.error("Couldn't load campus list.");
      }
    })();
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        campus_id: form.campus_id || null,
        note: form.note || null,
        expires_at: form.expires_in_days ? daysFromNow(form.expires_in_days) : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      };
      await api.post("/volunteer-invites", payload);
      toast.success("Invite created.");
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revoke code ${inv.code}? It will stop working immediately.`)) return;
    try {
      await api.post(`/volunteer-invites/${inv.id}/revoke`);
      toast.success("Invite revoked.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const reactivate = async (inv) => {
    try {
      await api.post(`/volunteer-invites/${inv.id}/reactivate`);
      toast.success("Invite reactivated.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const copyLink = (inv) => {
    const url = `${window.location.origin}/volunteer/register?code=${inv.code}`;
    navigator.clipboard.writeText(url);
    toast.success("Registration link copied.");
  };

  const visible = invites.filter((i) => showRevoked || !i.revoked_at);

  return (
    <div className="space-y-6" data-testid="volunteer-invites-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Volunteers · Invite Codes
        </p>
        <h1 className="text-4xl font-serif text-forest-900 mt-1">Volunteer Invite Codes</h1>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
          Generate short codes to hand out at campus gatherings or paste into a
          welcome text. Each invite carries a campus assignment — new volunteers
          are joined to that campus automatically.
        </p>
      </div>

      <form onSubmit={create} className="patria-card p-6 space-y-4" data-testid="new-invite-form">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif text-2xl text-forest-900">Create A New Invite</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="patria-label">Campus</label>
            <select
              className="patria-input w-full"
              value={form.campus_id}
              onChange={(e) => setForm({ ...form, campus_id: e.target.value })}
              data-testid="invite-campus"
            >
              <option value="">Unassigned</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="patria-label">Expires In (Days)</label>
            <input
              type="number"
              min={1}
              max={365}
              className="patria-input w-full"
              value={form.expires_in_days}
              onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
              data-testid="invite-expiry"
            />
          </div>
          <div>
            <label className="patria-label">Max Uses (Blank = Unlimited)</label>
            <input
              type="number"
              min={1}
              className="patria-input w-full"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              data-testid="invite-max-uses"
              placeholder="Unlimited"
            />
          </div>
          <div>
            <label className="patria-label">Note</label>
            <input
              className="patria-input w-full"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Sunday morning volunteer wave"
              data-testid="invite-note"
            />
          </div>
        </div>
        <button type="submit" disabled={creating} className="patria-btn-primary" data-testid="btn-create-invite">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
          {creating ? "Generating…" : "Generate Invite Code"}
        </button>
      </form>

      <div className="flex items-center justify-between">
        <p className="text-sm text-charcoal-soft">
          {invites.length} invite{invites.length === 1 ? "" : "s"} in total.
        </p>
        <label className="flex items-center gap-2 text-sm text-charcoal-soft">
          <input
            type="checkbox"
            checked={showRevoked}
            onChange={(e) => setShowRevoked(e.target.checked)}
            data-testid="show-revoked"
          />
          Show revoked
        </label>
      </div>

      <div className="patria-card overflow-hidden">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>Code</th>
              <th>Campus</th>
              <th>Note</th>
              <th>Uses</th>
              <th>Expires</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="italic text-charcoal-muted">Loading…</td></tr>}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={7} className="italic text-charcoal-muted">No invites yet.</td></tr>
            )}
            {!loading && visible.map((inv) => (
              <tr key={inv.id} className={inv.revoked_at ? "opacity-60" : ""} data-testid={`invite-row-${inv.id}`}>
                <td>
                  <span className="font-mono tracking-widest text-forest-900">{inv.code}</span>
                </td>
                <td className="text-sm">{inv.campus_name || "—"}</td>
                <td className="text-xs text-charcoal-muted max-w-xs truncate">{inv.note || ""}</td>
                <td className="text-sm">
                  {inv.uses_count}
                  {inv.max_uses ? ` / ${inv.max_uses}` : ""}
                </td>
                <td className="text-xs text-charcoal-muted">
                  {(inv.expires_at || "").slice(0, 10) || "Never"}
                </td>
                <td>
                  {inv.revoked_at ? (
                    <span className="patria-chip-terra">Revoked</span>
                  ) : inv.expired ? (
                    <span className="patria-chip-terra">Expired</span>
                  ) : inv.exhausted ? (
                    <span className="patria-chip-terra">Used Up</span>
                  ) : (
                    <span className="patria-chip-green">Active</span>
                  )}
                </td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  <button
                    className="patria-btn-ghost"
                    onClick={() => copyLink(inv)}
                    data-testid={`btn-copy-link-${inv.id}`}
                  >
                    <Copy className="h-4 w-4" strokeWidth={1.5} /> Copy Link
                  </button>
                  {inv.revoked_at ? (
                    <button className="patria-btn-ghost text-forest-700" onClick={() => reactivate(inv)} data-testid={`btn-reactivate-${inv.id}`}>
                      <PlayCircle className="h-4 w-4" strokeWidth={1.5} /> Reactivate
                    </button>
                  ) : (
                    <button className="patria-btn-ghost text-terracotta-600" onClick={() => revoke(inv)} data-testid={`btn-revoke-${inv.id}`}>
                      <PauseCircle className="h-4 w-4" strokeWidth={1.5} /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
